import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// Cache key for localStorage
const COUNTRY_CACHE_KEY = "outskill_country_code";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const FALLBACK_CACHE_DURATION = 1 * 60 * 1000; // 1 minute

// Get cached country code
const getCachedCountry = () => {
  try {
    const cached = localStorage.getItem(COUNTRY_CACHE_KEY);
    if (cached) {
      const { countryCode, timestamp, isFallback } = JSON.parse(cached);
      const ttl = isFallback ? FALLBACK_CACHE_DURATION : CACHE_DURATION;
      // Check if cache is still valid
      if (Date.now() - timestamp < ttl) {
        return countryCode;
      }
    }
  } catch {
    // Ignore cache errors
  }
  return null;
};

// Save country code to cache
const setCachedCountry = (countryCode, options = {}) => {
  const { isFallback = false } = options;
  try {
    localStorage.setItem(
      COUNTRY_CACHE_KEY,
      JSON.stringify({
        countryCode,
        timestamp: Date.now(),
        isFallback,
      })
    );
  } catch {
    // Ignore cache errors
  }
};

// Async thunk to detect country
export const detectCountry = createAsyncThunk(
  "country/detectCountry",
  async () => {
    // Check cache first
    const cachedCountry = getCachedCountry();
    if (cachedCountry) {
      return cachedCountry;
    }

    try {
      const response = await fetch("https://ipapi.co/json/");
      
      // Handle rate limiting (429) silently
      if (response.status === 429) {
        const cached = getCachedCountry();
        if (cached) return cached;
        // Cache short-lived fallback to avoid repeated rate-limited calls
        setCachedCountry("IN", { isFallback: true });
        return "IN"; // Fallback to India
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const countryCode = data.country_code || "IN";
      
      // Cache the result
      setCachedCountry(countryCode);
      
      return countryCode;
    } catch {
      // Try to use cached value on error
      const cached = getCachedCountry();
      if (cached) return cached;
      
      // Cache short-lived fallback to avoid repeated failing calls
      setCachedCountry("IN", { isFallback: true });
      // Fallback to India if detection fails (silently)
      return "IN";
    }
  },
);

const countrySlice = createSlice({
  name: "country",
  initialState: {
    countryCode: null,
    isIndia: false,
    loading: true,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(detectCountry.pending, (state) => {
        state.loading = true;
      })
      .addCase(detectCountry.fulfilled, (state, action) => {
        state.loading = false;
        state.countryCode = action.payload;
        state.isIndia = action.payload === "IN";
      })
      .addCase(detectCountry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        // Fallback to India
        state.countryCode = "IN";
        state.isIndia = true;
      });
  },
});

export default countrySlice.reducer;
